import { parseRawAmount } from '../../../utils/parsing'
import { isFailedCall } from '../../../utils/multicall'
import { createMarketUid } from '../../../utils'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import { createMultiAccountTypeUserState } from '../utils'
import {
  LenderCrossPoolMeta,
  LenderToLenderCrossPoolMeta,
  UserData,
} from '../utils/types'
import { tellerLenderKey } from '../../public-data/teller/convertPublic'
import { getCachedTellerBids, TELLER_CALLS_PER_BID } from './userCallBuild'

const nowSec = () => Math.floor(Date.now() / 1000)

const big = (v: any): bigint => {
  try {
    if (typeof v === 'bigint') return v
    if (typeof v === 'number' || typeof v === 'string') return BigInt(v)
  } catch {}
  return 0n
}

/** Field access for decoded tuples that may arrive named or positional. */
const field = (res: any, name: string, idx: number): any =>
  res?.[name] ?? res?.[idx]

/**
 * Returns a [converter, expectedCallCount] tuple for Teller user data.
 *
 * Layout mirrors `buildTellerUserCall`: for each discovered bid (in cache
 * order) four results — `bids`, `calculateAmountOwed`, `getCollateralAmount`,
 * `isLoanDefaulted`. Bids are ISOLATED (each is its own collateral + fixed debt)
 * and a user holds many per pool, so each bidId becomes its OWN sub-account
 * under the pool key `TELLER_<POOL_ADDR>` (River-style rows, but keyed by bidId
 * instead of `'0'`):
 *
 *  - collateral row: deposits = the bid's escrowed collateral;
 *  - loan row: `debtStable` = `calculateAmountOwed` (principal + interest) — a
 *    fixed-term debt goes on the stable slot (Exactly/Lista convention). The
 *    `term` detail (`ListaTermLoan`) carries `loanId = bidId` (the exact
 *    `repayLoanFull` target), the fixed APR, and the maturity
 *    (`acceptedTimestamp + loanDuration`).
 */
export const getTellerUserDataConverter = (
  _lender: string,
  chainId: string,
  account: string,
  meta?: LenderToLenderCrossPoolMeta,
): [(data: any[]) => { [lender: string]: UserData } | undefined, number] => {
  const discovery = getCachedTellerBids(chainId, account)
  const kept = discovery?.bids ?? []
  const expected = kept.length * TELLER_CALLS_PER_BID

  return [
    (data: any[]) => {
      if (expected === 0 || !data || data.length < expected) return undefined
      const now = nowSec()

      // Group per pool: lenderKey → { bidId → { collUid, loanUid rows } }.
      const perPool: {
        [lenderKey: string]: {
          positions: Record<string, any>
          modes: Record<string, number>
          hist: Record<
            string,
            { totalDeposits24h: number; totalDebt24h: number }
          >
          metaMap: LenderCrossPoolMeta
        }
      } = {}

      kept.forEach((b, i) => {
        const base = i * TELLER_CALLS_PER_BID
        const bidRes = data[base]
        const owed = data[base + 1]
        const collAmount = big(data[base + 2])
        const defaulted = !!data[base + 3]
        // The failure sentinel is a non-empty string, so it survives a plain
        // truthiness check — a dropped read would otherwise report the loan as
        // fully repaid.
        if (isFailedCall(bidRes) || isFailedCall(owed) || !bidRes) return

        const pool = b.pool
        const lenderKey = tellerLenderKey(pool.pool)
        const metaMap: LenderCrossPoolMeta | undefined = meta?.[lenderKey]
        if (!metaMap) return

        const principalAddr = pool.principal.toLowerCase()
        const collAddr = pool.collateral.toLowerCase()
        const loanUid = createMarketUid(chainId, lenderKey, principalAddr)
        const collUid = createMarketUid(chainId, lenderKey, collAddr)
        const loanMeta = metaMap[loanUid]
        const collMeta = metaMap[collUid]

        const principalDecimals =
          loanMeta?.asset?.decimals ?? pool.principalDecimals
        const collDecimals =
          collMeta?.asset?.decimals ?? pool.collateralDecimals

        const loanDisplay = loanMeta ? getDisplayPrice(loanMeta) : 0
        const loanOracle = loanMeta ? getOraclePrice(loanMeta) : 0
        const loanHist = loanMeta?.price?.priceUsd24h ?? loanDisplay
        const collDisplay = collMeta ? getDisplayPrice(collMeta) : 0
        const collOracle = collMeta ? getOraclePrice(collMeta) : 0
        const collHist = collMeta?.price?.priceUsd24h ?? collDisplay

        // Owed = principal + interest (raw principal-token units).
        const owedPrincipal = big(field(owed, 'principal', 0))
        const owedInterest = big(field(owed, 'interest', 1))
        const owedTotal = owedPrincipal + owedInterest
        if (owedTotal === 0n && collAmount === 0n) return

        // Bid struct → maturity + APR.
        const loanDetails = field(bidRes, 'loanDetails', 5)
        const terms = field(bidRes, 'terms', 6)
        const acceptedTs = Number(
          big(field(loanDetails, 'acceptedTimestamp', 4)),
        )
        const loanDuration = Number(big(field(loanDetails, 'loanDuration', 6)))
        const aprBps = Number(big(field(terms, 'APR', 2)))
        const maturity = acceptedTs > 0 ? acceptedTs + loanDuration : undefined
        const isMatured =
          defaulted || (maturity !== undefined && maturity < now)

        const collStr = parseRawAmount(collAmount.toString(), collDecimals)
        const collNum = Number(collStr)
        const debtStr = parseRawAmount(owedTotal.toString(), principalDecimals)
        const debtNum = Number(debtStr)
        const interestStr = parseRawAmount(
          owedInterest.toString(),
          principalDecimals,
        )

        const bucket = (perPool[lenderKey] ??= {
          positions: {},
          modes: {},
          hist: {},
          metaMap,
        })

        bucket.positions[b.bidId] = {
          [collUid]: {
            marketUid: collUid,
            underlying: collAddr,
            deposits: collStr,
            debt: '0',
            debtStable: '0',
            depositsUSD: collNum * collDisplay,
            debtUSD: 0,
            debtStableUSD: 0,
            depositsUSDOracle: collNum * collOracle,
            debtUSDOracle: 0,
            debtStableUSDOracle: 0,
            stableBorrowRate: '0',
            collateralEnabled: true,
            claimableRewards: 0,
          },
          [loanUid]: {
            marketUid: loanUid,
            underlying: principalAddr,
            deposits: '0',
            debt: '0',
            // Fixed-term debt → stable slot (Exactly/Lista convention).
            debtStable: debtStr,
            depositsUSD: 0,
            debtUSD: 0,
            debtStableUSD: debtNum * loanDisplay,
            depositsUSDOracle: 0,
            debtUSDOracle: 0,
            debtStableUSDOracle: debtNum * loanOracle,
            stableBorrowRate: String(aprBps / 100),
            collateralEnabled: false,
            claimableRewards: 0,
            // Fixed-term loan detail — `loanId = bidId` is the repayLoanFull target.
            term: {
              loanId: b.bidId,
              isDynamic: false,
              debt: debtStr,
              apr: aprBps / 100,
              maturity,
              accruedInterest: interestStr,
              isMatured,
            },
          },
        }
        bucket.modes[b.bidId] = 0
        bucket.hist[b.bidId] = {
          totalDeposits24h: collNum * collHist,
          totalDebt24h: debtNum * loanHist,
        }
      })

      const out: { [lender: string]: UserData } = {}
      for (const [lenderKey, bucket] of Object.entries(perPool)) {
        if (Object.keys(bucket.positions).length === 0) continue
        const userData = createMultiAccountTypeUserState(
          {
            chainId,
            account,
            lendingPositions: bucket.positions,
            modes: bucket.modes,
          } as any,
          bucket.metaMap,
          bucket.hist,
        )
        if (userData && userData.data.length > 0) out[lenderKey] = userData
      }

      return Object.keys(out).length > 0 ? out : undefined
    },
    expected,
  ]
}
